import dynamic from 'next/dynamic';
import { produce } from 'immer';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '@/redux';
import { useFeature } from '@/hooks';
import { isUserEmpty } from '@/utils/comparisonUtils';
import { checkHoverMenu, setHeaderOptions } from '@/utils/headerUtils';
import { useScreenSizeContext } from '@/components/ScreenSize.context';

import NavBarHomeLink from '@/components/layout/navbar/NavBarHomeLink';

const HeaderLink = dynamic(() => import('@/components/layout/header/HeaderLink'));
HeaderLink.displayName = 'HeaderLink';
const TrustedBadge = dynamic(() => import('@/components/common/TrustedBadge'));
TrustedBadge.displayName = 'TrustedBadge';

import styles from './HeaderDesktop.module.scss';
import { FEATURES } from '@/constants/features';
import { HEADER, HEADER_TYPE } from '@/constants/header';
import { STORE_ID } from '@/constants/stores';

export const HeaderDesktop = ({ storeConf, logo, backgroundColor, headerType }) => {
  const isCampaignFeatureEnabled = useFeature(storeConf, FEATURES.PRIMEUR_CAMPAIGN);
  const { isLargeDesktop } = useScreenSizeContext();

  const { ratingValue } = useAppSelector(({ root }) => root);
  const { loggedUser } = useAppSelector(({ auth }) => auth);
  const { settings: userSettings } = useAppSelector(({ user }) => user);

  const [options, setOptions] = useState(setHeaderOptions(headerType));
  const [hoverMenus, setHoverMenus] = useState({
    [HEADER.ADMIN]: { isOpen: false, forceClose: false },
    [HEADER.ACCOUNT]: { isOpen: false, forceClose: false },
    [HEADER.CART]: { isOpen: false, forceClose: false },
  });

  const isUserConnected = useMemo(() => !isUserEmpty(loggedUser), [loggedUser]);

  useEffect(() => {
    setOptions(setHeaderOptions(headerType));
  }, [headerType]);

  const colorClass = backgroundColor ? styles[backgroundColor] : '';

  const headerStyle = {
    backgroundColor: backgroundColor ? null : storeConf.headerBackgroundColor,
    color: storeConf.headerFontColor || '#e5e5e5'
  };

  const toggleHoverMenu = (currentMenu, isOpen) => {
    const draftHoverMenus = produce(hoverMenus, (draft) => {
      if (currentMenu === HEADER.ACCOUNT) {
        draft[currentMenu].isOpen = isOpen && isUserConnected;
      } else {
        draft[currentMenu].isOpen = isOpen;
      }
      // Force close of other menu
      Object.keys(draft).forEach(
        (menu) => draft[menu].forceClose = (menu !== currentMenu)
      );
    });

    setHoverMenus(draftHoverMenus);
  };

  return (
    <div className={`${styles['header-top-bar']} ${colorClass}`} style={headerStyle} data-test-id="header-top-bar" data-rbf="header-top-bar">
      <div className={`container ${styles['container-width']}`}>
        <div className={`${styles['container']} ${isLargeDesktop ? styles['container-xlarge'] : ''}`}>
          <div className={styles['container-left']}>
            {options.withLeaveStorage && <HeaderLink type={HEADER.LEAVE_CELLAR} />}
            {options.withReturnToAccount && <HeaderLink type={HEADER.RETURN_TO_ACCOUNT} />}
            {options.withReturnToExpro && <HeaderLink type={HEADER.RETURN_TO_EXPRO} />}
            {options.withContact && storeConf.storeId === STORE_ID.USA && <HeaderLink type={HEADER.CONTACT} />}
            {options.withHelp && <HeaderLink type={HEADER.QUESTION} />}
            {(options.withTrustedShop && storeConf.trustLink && ratingValue) ? (
              <TrustedBadge
                trustLink={storeConf.trustLink}
                ratingValue={ratingValue}
                isSmallImg
                isWhiteImg
                isSmallText
                fontColor={headerStyle.color}
              />
            ) : null}
          </div>

          <div className={styles['container-middle']}>
            <NavBarHomeLink logo={logo} mobile={false} backgroundColor={backgroundColor} isBlog={headerType === HEADER_TYPE.BLOG} />
          </div>

          <div className={styles['container-right']}>
            {options.withPrimeurAlert && isCampaignFeatureEnabled && (
              <HeaderLink type={HEADER.PRIMEUR} />
            )}
            {options.withDeliveryCountry &&
              <HeaderLink type={HEADER.DELIVERY_COUNTRY} />
            }
            {
              Object.keys(hoverMenus)
                .filter((hoverMenu) => (hoverMenu !== HEADER.ADMIN || (hoverMenu === HEADER.ADMIN && loggedUser?.isAdmin)) &&
                  checkHoverMenu(hoverMenu, options)
                )
                .map((hoverMenu) => (
                  <HeaderLink
                    key={hoverMenu}
                    type={hoverMenu}
                    isUserConnected={isUserConnected}
                    onOpen={() => toggleHoverMenu(hoverMenu, true)}
                    isOpen={hoverMenus[hoverMenu].isOpen}
                    onClose={() => toggleHoverMenu(hoverMenu, false)}
                    forceClose={hoverMenus[hoverMenu].forceClose}
                    userSettings={hoverMenu === HEADER.ADMIN ? userSettings : null}
                  />
                ))
            }
          </div>
        </div>
      </div>
    </div>
  );
};

HeaderDesktop.propTypes = {
  storeConf: PropTypes.object,
  logo: PropTypes.string,
  backgroundColor: PropTypes.string,
  headerType: PropTypes.string
};

export default HeaderDesktop;
